const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "select:not([disabled])",
  "input:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export function visibleFocusableElements(
  root: ParentNode = document,
): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
    (element) =>
      !element.hidden &&
      !element.closest("[hidden]") &&
      !element.closest("dialog:not([open])") &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.style.display !== "none",
  );
}

export function moveUiFocus(direction: 1 | -1): HTMLElement | undefined {
  const elements = visibleFocusableElements();
  if (elements.length === 0) return undefined;
  const currentIndex = elements.indexOf(document.activeElement as HTMLElement);
  const nextIndex =
    currentIndex < 0
      ? direction > 0
        ? 0
        : elements.length - 1
      : (currentIndex + direction + elements.length) % elements.length;
  const next = elements[nextIndex];
  next?.focus();
  return next;
}

export class UiNavigator {
  #animationFrame = 0;
  #previousDirection = 0;
  #previousAccept = false;
  #previousBack = false;

  constructor() {
    window.addEventListener("keydown", this.#onKeyDown);
    this.#animationFrame = requestAnimationFrame(this.#pollGamepad);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.#onKeyDown);
    cancelAnimationFrame(this.#animationFrame);
  }

  readonly #onKeyDown = (event: KeyboardEvent): void => {
    if (!uiNavigationIsActive()) return;
    if (
      document.activeElement instanceof HTMLInputElement ||
      document.activeElement instanceof HTMLSelectElement
    ) {
      return;
    }
    if (["ArrowDown", "ArrowRight"].includes(event.code)) {
      moveUiFocus(1);
      event.preventDefault();
    } else if (["ArrowUp", "ArrowLeft"].includes(event.code)) {
      moveUiFocus(-1);
      event.preventDefault();
    }
  };

  readonly #pollGamepad = (): void => {
    const gamepad = navigator.getGamepads?.()[0];
    if (gamepad && uiNavigationIsActive()) {
      const axis = gamepad.axes[1] ?? 0;
      const direction =
        gamepad.buttons[13]?.pressed || axis > 0.55
          ? 1
          : gamepad.buttons[12]?.pressed || axis < -0.55
            ? -1
            : 0;
      const accept = Boolean(gamepad.buttons[0]?.pressed);
      const back = Boolean(gamepad.buttons[1]?.pressed);
      if (direction !== 0 && this.#previousDirection === 0) {
        moveUiFocus(direction as 1 | -1);
      }
      if (accept && !this.#previousAccept) {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body) {
          active.click();
        } else {
          moveUiFocus(1)?.click();
        }
      }
      if (back && !this.#previousBack) activateBackAction();
      this.#previousDirection = direction;
      this.#previousAccept = accept;
      this.#previousBack = back;
    } else {
      this.#previousDirection = 0;
      this.#previousAccept = false;
      this.#previousBack = false;
    }
    this.#animationFrame = requestAnimationFrame(this.#pollGamepad);
  };
}

function uiNavigationIsActive(): boolean {
  if (!document.querySelector("#game-canvas")) return true;
  return Boolean(
    document.querySelector("dialog[open]") ||
      document.querySelector("#meta-panel:not([hidden])") ||
      document.querySelector("#game-overlay:not([hidden])"),
  );
}

function activateBackAction(): void {
  const back = document.querySelector<HTMLElement>(
    "[data-setup-back], [data-setup-cancel], [data-close-progress], [data-close-settings], [data-result-title]",
  );
  if (back && !back.closest("[hidden]")) back.click();
}
